import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { type ActivatedRouteSnapshot, type RouterStateSnapshot, TitleStrategy } from '@angular/router';

/**
 * Custom title strategy for the application.
 * Extends Angular's TitleStrategy to set page titles based on route data.
 * Adds "FourEnergy Event Hub | " prefix to all page titles.
 */
@Injectable({ providedIn: 'root' })
export class AppTitleService extends TitleStrategy {
  private readonly titleService = inject(Title);

  /**
   * Updates the document title based on the current route.
   * Searches for the most specific title in the route tree.
   * @param snapshot Current router state snapshot
   */
  override updateTitle(snapshot: RouterStateSnapshot): void {
    const title = this.findTitle(snapshot.root);

    if (title) {
      this.titleService.setTitle(`FourEnergy Event Hub | ${title}`);
    } else {
      this.titleService.setTitle('FourEnergy Event Hub');
    }
  }

  /**
   * Recursively searches for the most specific title in the route tree.
   * Starts from the current route and drills down through child routes.
   * @param snapshot The route snapshot to check for title data
   * @returns The most specific title found or undefined
   */
  findTitle(snapshot: ActivatedRouteSnapshot): string | undefined {
    const routeData = snapshot.data;
    const routeTitle = routeData.title;

    if (!routeTitle && snapshot.children.length > 0) {
      for (const child of snapshot.children) {
        const childTitle = this.findTitle(child);
        if (childTitle) {
          return childTitle;
        }
      }
      return undefined;
    }

    return routeTitle as string | undefined;
  }
}
